import { outputTemplateMetadataFragment } from '@eluve/frontend-appointment-hooks';
import { graphql } from '@eluve/graphql.tada';

export const summaryContentFragment = graphql(
  `
    fragment SummaryContent on Appointments @_unmask {
      __typename
      id
      mode
      humanOutputs(
        where: { output: { outputType: { _eq: SOAP_NOTE } } }
        limit: 1
        orderBy: { output: { createdAt: DESC } }
      ) {
        __typename
        humanOutputId
        output {
          __typename
          id
          content
          userId
          llm_output {
            __typename
            id
            content
            metadata {
              ...OutputTemplateMetadata
            }
            modelPromptTemplate {
              __typename
              id
              template {
                __typename
                id
                name
                outputType
              }
            }
          }
        }
        output_template {
          __typename
          outputTemplateId
          variantNumber
          template
        }
      }
    }
  `,
  [outputTemplateMetadataFragment],
);

export const draftContentFragment = graphql(
  `
    fragment DraftContent on Appointments @_unmask {
      __typename
      id
      doctor_interaction {
        __typename
        appointmentId
        noteSignedAt
        additionalNotes
        externalChartUrl
        externalEhrSyncStatus
      }
      ...SummaryContent
    }
  `,
  [summaryContentFragment],
);

export const billingCodesFragment = graphql(`
  fragment BillingCodes on Appointments @_unmask {
    __typename
    id
    billingCodes {
      __typename
      id
      medical_code {
        __typename
        id
        code
        description
      }
    }
  }
`);

export const ehrDiffContentFragment = graphql(
  `
    fragment EhrDiffContent on Appointments @_unmask {
      __typename
      id
      external_charts {
        __typename
        id
        rawData
        external_ehr {
          __typename
          id
          vendor
          domain
        }
      }
      ...DraftContent
      ...BillingCodes
    }
  `,
  [draftContentFragment, billingCodesFragment],
);

export const goldenSummaryContentFragment = graphql(`
  fragment GoldenSummaryContent on AppointmentGoldenNotes {
    __typename
    id
    content
    status
    createdAt
    updatedAt
  }
`);

export const appointmentGoldenSummaryContentFragment = graphql(
  `
    fragment AppointmentGoldenSummaryContent on Appointments @_unmask {
      __typename
      id
      appointment_golden_notes(
        where: { outputType: { _eq: SOAP_NOTE }, id: { _eq: $activeNoteId } }
        orderBy: { createdAt: DESC }
      ) {
        id
        content
        status
        createdAt
        updatedAt
      }
    }
  `,
  [goldenSummaryContentFragment],
);

export const getGoldenNoteSummary = graphql(
  `
    query getGoldenNoteSummary(
      $tenantId: uuid!
      $appointmentId: uuid!
      $activeNoteId: uuid
    ) {
      appointmentsByPk(tenantId: $tenantId, id: $appointmentId) {
        __typename
        id
        mode
        ...AppointmentGoldenSummaryContent
        ...SummaryContent
      }
    }
  `,
  [appointmentGoldenSummaryContentFragment, summaryContentFragment],
);

export const createGoldenSummaryMutation = graphql(`
  mutation createGoldenSummary(
    $appointmentId: uuid!
    $tenantId: uuid!
    $outputType: LlmOutputTypesEnum!
    $content: jsonb!
  ) {
    insertAppointmentGoldenNotesOne(
      object: {
        appointmentId: $appointmentId
        tenantId: $tenantId
        outputType: $outputType
        content: $content
        status: "DRAFT"
      }
    ) {
      __typename
      id
      content
      status
      createdAt
      updatedAt
    }
  }
`);

export const updateGoldenSummaryMutation = graphql(`
  mutation updateGoldenSummaryMutation(
    $id: uuid!
    $path: _text!
    $value: jsonb!
  ) {
    updateGoldenSummaryContent(args: { _id: $id, path: $path, value: $value }) {
      __typename
      id
      content
      status
      createdAt
      updatedAt
    }
  }
`);

export const finalizeGoldenSummaryMutation = graphql(`
  mutation finalizeGoldenSummary($id: uuid!, $tenantId: uuid!) {
    updateAppointmentGoldenNotesByPk(
      pkColumns: { id: $id, tenantId: $tenantId }
      _set: { status: "COMPLETED" }
    ) {
      __typename
      id
      content
      status
      createdAt
      updatedAt
    }
  }
`);

export const goldenFactFragment = graphql(`
  fragment GoldenFact on AppointmentGoldenFacts @_unmask {
    __typename
    id
    tenantId
    appointmentId
    statement
    expectedOutput
    weightMultiplier
    isVerified
    createdAt
    updatedAt
  }
`);

export const orderedGoldenFactListFragment = graphql(
  `
    fragment OrderedGoldenFactList on Appointments @_unmask {
      appointment_golden_facts(orderBy: { createdAt: DESC }) {
        ...GoldenFact
      }
    }
  `,
  [goldenFactFragment],
);

export const getGoldenFactsQuery = graphql(
  `
    query getGoldenFacts($tenantId: uuid!, $appointmentId: uuid!) {
      appointmentsByPk(tenantId: $tenantId, id: $appointmentId) {
        __typename
        id
        ...OrderedGoldenFactList
      }
    }
  `,
  [orderedGoldenFactListFragment],
);

export const createGoldenFactMutation = graphql(
  `
    mutation createGoldenFact(
      $tenantId: uuid!
      $appointmentId: uuid!
      $statement: String!
      $expectedOutput: Boolean!
      $weightMultiplier: numeric!
    ) {
      insertAppointmentGoldenFactsOne(
        object: {
          tenantId: $tenantId
          appointmentId: $appointmentId
          statement: $statement
          expectedOutput: $expectedOutput
          weightMultiplier: $weightMultiplier
          isVerified: false
        }
      ) {
        ...GoldenFact
      }
    }
  `,
  [goldenFactFragment],
);

export const updateGoldenFactMutation = graphql(
  `
    mutation updateGoldenFact(
      $id: uuid!
      $tenantId: uuid!
      $statement: String
      $expectedOutput: Boolean
      $weightMultiplier: numeric
      $isVerified: Boolean
    ) {
      updateAppointmentGoldenFactsByPk(
        pkColumns: { id: $id, tenantId: $tenantId }
        _set: {
          statement: $statement
          expectedOutput: $expectedOutput
          weightMultiplier: $weightMultiplier
          isVerified: $isVerified
        }
      ) {
        ...GoldenFact
      }
    }
  `,
  [goldenFactFragment],
);

export const deleteGoldenFactMutation = graphql(`
  mutation deleteGoldenFact($id: uuid!, $tenantId: uuid!) {
    deleteAppointmentGoldenFactsByPk(id: $id, tenantId: $tenantId) {
      id
    }
  }
`);
